import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
// Avoid importing framer-motion in the Navbar (above-the-fold). Use CSS
// transitions and simple transform utilities instead to reduce initial JS.
import { Home, Building, Mail, PlusCircle, LogIn, LogOut } from 'lucide-react';
import { useAuth } from '../AuthContext';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { name: 'Inicio', path: '/', icon: Home },
    { name: 'Propiedades', path: '/propiedades', icon: Building },
    { name: 'Contacto', path: '/contacto', icon: Mail },
  ];

  const NavButton = ({ to, icon: Icon, label, onClick }) => {
    const content = (
      <div
        className="flex items-center space-x-2 text-primary hover:text-secondary transition-transform duration-200 transform hover:-translate-y-0.5 hover:scale-105 cursor-pointer"
      >
        <Icon className="w-5 h-5" aria-hidden="true" />
        <span className="font-medium text-sm hidden sm:block">{label}</span>
      </div>
    );

    // If a `to` prop is provided, render a Link. Otherwise render a button for actions like Logout.
    if (to) {
      return (
        <Link to={to} aria-label={label} title={label} onClick={onClick}>
          {content}
        </Link>
      );
    }

    return (
      <button type="button" aria-label={label} title={label} onClick={onClick} className="p-0 bg-transparent border-0">
        {content}
      </button>
    );
  };

  return (
    <nav
      className="bg-white shadow-lg px-6 py-3 w-full flex items-center justify-between border-b border-gray-200"
      aria-label="Main navigation"
    >
      {/* Logo and Main Navigation */}
      <div className="flex items-center space-x-8"> {/* Increased space-x */} 
        <Link to="/" className="flex items-center space-x-2">
          <picture>
            <source
              type="image/avif"
              srcSet="/assets/logo-48.avif 48w, /assets/logo-96.avif 96w, /assets/logo-120.avif 120w, /assets/logo-240.avif 240w"
              sizes="48px"
            />
            <source
              type="image/webp"
              srcSet="/assets/logo-48.webp 48w, /assets/logo-96.webp 96w, /assets/logo-120.webp 120w, /assets/logo-240.webp 240w"
              sizes="48px"
            />
            <img
              src="/assets/logo-120.png"
              srcSet="/assets/logo-48.png 48w, /assets/logo-96.png 96w, /assets/logo-120.png 120w, /assets/logo-240.png 240w"
              alt="Casa Del Príncipe Logo"
              title="/"
              className="h-10 w-auto object-contain"
              decoding="async"
              width="120" height="40"
            />
          </picture>
            <span className="text-primary text-xl font-sans hidden md:block uppercase">Casa Del Príncipe</span> {/* Added text logo, now uppercase */}
          </Link>

          <div className="hidden md:flex items-center space-x-6"> {/* Hide on small screens */}
          {navItems.map(({ name, path, icon }) => (
            <NavButton key={name} to={path} icon={icon} label={name} />
          ))}
        </div>
      </div>

      {/* Session Buttons */}
      <div className="flex items-center space-x-4">
        {user ? (
          <>
            <NavButton to="/añadir-propiedad" icon={PlusCircle} label="Añadir Propiedad" />
            <NavButton icon={LogOut} label="Logout" onClick={handleLogout} />
          </>
        ) : (
          <NavButton to="/login" icon={LogIn} label="Login" />
        )}
      </div>
  </nav>
  );
};

export default Navbar;
